// De korte terugblik op zondag (B40, AI golf C): what went well, what stayed behind, and one tip. Kept, so it can
// be read again in Meer > Terugblik, also offline.
import { useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { weekStart } from "@shared/routines";
import { displayDate, isoWeekday } from "@shared/time";
import { aiReview, useAiEnabled } from "../lib/ai";
import { db, type WeeklyReview } from "../lib/db";
import { Button } from "./ui";

export function ReviewCard({ review }: { review: WeeklyReview }) {
  const s = review.stats;
  return (
    <article className="rounded-2xl border border-mist bg-surface p-3">
      <h3 className="font-semibold">Week van {displayDate(review.week_start)}</h3>
      <p className="mt-1 [overflow-wrap:anywhere]">{review.summary}</p>
      <p className="mt-2 rounded-xl bg-paper p-2 [overflow-wrap:anywhere]">
        <span className="font-semibold">Tip: </span>
        {review.tip}
      </p>
      <p className="mt-2 text-sm text-ink-soft tabular">
        {s.blocksDone} van {s.blocksTotal} blokken klaar. Gym {s.gym} keer. {s.tasksDone} taken af.
        {s.mostMissed.length ? ` Bleef liggen: ${s.mostMissed.map((m) => m.title).join(", ")}.` : ""}
      </p>
      {s.moods && s.moods.good + s.moods.ok + s.moods.hard > 0 && (
        <p className="mt-1 text-sm text-ink-soft tabular">
          Stemming: {s.moods.good} goed, {s.moods.ok} gewoon, {s.moods.hard} zwaar.
        </p>
      )}
    </article>
  );
}

/** On Sunday on Vandaag: the look back of this week, or the button to make it. */
export function SundayReview({ date }: { date: string }) {
  const ai = useAiEnabled();
  const monday = weekStart(date);
  const review = useLiveQuery(
    async () => (await db.weekly_reviews.where("week_start").equals(monday).toArray()).find((r) => !r.deleted_at) ?? null,
    [monday],
  );
  const [busy, setBusy] = useState(false);
  if (isoWeekday(date) !== 7 || review === undefined) return null;
  if (review)
    return (
      <section aria-label="Terugblik op je week" className="mb-6">
        <ReviewCard review={review} />
      </section>
    );
  if (!ai) return null;
  return (
    <section aria-label="Terugblik op je week" className="mb-6 rounded-2xl border-2 border-sun bg-surface p-3">
      <p className="mb-2">Zondag: tijd voor een korte terugblik op je week.</p>
      <Button
        className="w-full"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await aiReview(monday);
          } catch {
            toast("De terugblik kan alleen met verbinding.");
          } finally {
            setBusy(false);
          }
        }}
      >
        <Sparkles aria-hidden className="size-5" />
        {busy ? "Bezig met terugkijken" : "Terugblik maken"}
      </Button>
    </section>
  );
}
